import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { CONTACT } from '../constants/images';
import { useToast } from '../hooks/use-toast';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you within 24 hours.",
    });
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const locations = [
    {
      name: 'Bombay Hospital & MRC, Marine Lines',
      position: 'Primary Location - Associate Consultant',
      address: 'Interventional Radiology, 3rd Floor, New Wing, Marine Lines, Mumbai',
      timing: 'Mon-Fri: 12:00 PM - 7:00 PM | Sat: 12:00 PM - 4:00 PM',
      isPrimary: true
    },
    {
      name: 'Sushrut Hospital, Chembur',
      position: 'Consultant Interventional Radiologist',
      address: 'Chembur, Mumbai',
      timing: 'On call - Book via WhatsApp or phone',
      isPrimary: false
    },
    {
      name: 'MGM New Bombay Hospital, Vashi',
      position: 'Interventional Radiologist',
      address: 'Vashi, Navi Mumbai',
      timing: 'On call - Book via WhatsApp or phone',
      isPrimary: false
    },
    {
      name: 'Adi Arogyam Hospital, Vikhroli',
      position: 'Consultant Interventional Radiologist',
      address: 'Vikhroli, Mumbai',
      timing: 'On call - Book via WhatsApp or phone',
      isPrimary: false
    },
    {
      name: 'Vikas Nursing Home, Govandi',
      position: 'Consultant Interventional Radiologist',
      address: 'Govandi, Mumbai',
      timing: 'On call - Book via WhatsApp or phone',
      isPrimary: false
    }
  ];

  return (
    <>
      <SEO 
        title="Book an Appointment with Dr. Punit Mahajan - Interventional Radiologist in Mumbai"
        description="Book a consultation with Dr. Punit Mahajan at Bombay Hospital Marine Lines or other Mumbai locations. Call +91 98192 15761. Email: drpunit.ir@gmail.com. Interventional Radiology in Mumbai."
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-950 to-slate-900 text-white py-20 relative overflow-hidden"><div className="absolute inset-0 pattern-hero opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-text-reveal">
            Book a Consultation
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto animate-text-reveal" style={{ animationDelay: "300ms" }}>
            Get in touch with Dr. Punit Mahajan - Interventional Radiologist in Mumbai
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16 bg-cream-textured relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="gold-ornament">Get In Touch</div>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="border-2 border-amber-400 hover:border-amber-600 hover:shadow-xl transition-all bg-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-50 border-2 border-amber-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Phone</h3>
                <a href={`tel:${CONTACT.phone}`} className="text-blue-900 hover:text-amber-600 font-semibold text-lg block mb-2">
                  {CONTACT.phone}
                </a>
                <p className="text-sm text-slate-600">Mon-Sat, 9 AM - 8 PM</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-amber-400 hover:border-amber-600 hover:shadow-xl transition-all bg-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-50 border-2 border-amber-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">WhatsApp</h3>
                <a 
                  href={`https://wa.me/${CONTACT.whatsapp}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-900 hover:text-amber-600 font-semibold text-lg block mb-2"
                >
                  {CONTACT.whatsapp}
                </a>
                <p className="text-sm text-slate-600">Quick Response</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-amber-400 hover:border-amber-600 hover:shadow-xl transition-all bg-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-50 border-2 border-amber-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Email</h3>
                <a href={`mailto:${CONTACT.email}`} className="text-blue-900 hover:text-amber-600 font-semibold text-lg block mb-2">
                  {CONTACT.email}
                </a>
                <p className="text-sm text-slate-600">Response within 24 hours</p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Send Us a Message</h2>
              <p className="text-slate-600 mb-8">
                Have a question about your condition or a procedure? Not sure if Interventional Radiology is right for you? Leave your details below and Dr. Punit Mahajan's team in Mumbai will reach out.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Your Name *</label>
                  <Input 
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                  <Input 
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number *</label>
                  <Input 
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 XXXXX XXXXX"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message *</label>
                  <Textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your condition or query..."
                    className="w-full min-h-[150px]"
                  />
                </div>
                
                <Button type="submit" size="lg" className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                  <Send className="w-5 h-5 mr-2" />
                  Send Message
                </Button>
              </form>
            </div>

            <div>
              <div className="bg-gradient-to-br from-blue-900 to-slate-900 text-white p-8 rounded-2xl">
                <h3 className="text-2xl font-bold mb-6">Quick Information</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-amber-500 mb-2">Response Time</h4>
                    <p className="text-slate-300">We typically respond to all inquiries within 24 hours during working days.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-500 mb-2">Emergency Cases</h4>
                    <p className="text-slate-300">For emergency cases, please call directly at {CONTACT.phone}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-500 mb-2">Bring Your Reports</h4>
                    <p className="text-slate-300">For your first consultation, please bring recent investigation reports - ultrasound, Doppler, CT, or blood reports.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <MapPin className="w-12 h-12 text-amber-600 mx-auto mb-4" />
            <div className="gold-ornament mb-4">Locations</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Consulting Locations in Mumbai
            </h2>
            <p className="text-lg text-slate-600">Dr. Punit Mahajan consults at multiple locations across Mumbai</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((location, index) => (
              <Card key={index} className={`${location.isPrimary ? 'border-2 border-amber-600' : 'border'} hover:shadow-lg transition-shadow`}>
                <CardContent className="p-6">
                  {location.isPrimary && (
                    <span className="inline-block bg-amber-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-3">
                      PRIMARY LOCATION
                    </span>
                  )}
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{location.name}</h3>
                  <p className="text-sm text-blue-900 font-medium mb-3">{location.position}</p>
                  <div className="space-y-2 text-slate-600">
                    <div className="flex items-start">
                      <MapPin className="w-4 h-4 mr-2 mt-1 flex-shrink-0 text-amber-600" />
                      <span className="text-sm">{location.address}</span>
                    </div>
                    <div className="flex items-start">
                      <Clock className="w-4 h-4 mr-2 mt-1 flex-shrink-0 text-amber-600" />
                      <span className="text-sm">{location.timing}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
